import React, { useRef, useState} from "react";
import q2tLogo from "../assets/img/q2t-logo.png";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Pagination, Navigation } from "swiper/modules";
import CardItem from "../components/CardForm/CardItem";
import CardRow from "../components/CardForm/CardRow";
import { useCard } from "../context/CardContext"
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL } from '../config/environments';

export default function ViewCardPage() {
    const { cardData } = useCard();
    const [selectedIndex, setSelectedIndex] = useState(null);
    const navigate = useNavigate();
    const swiperRef = useRef(null);

    const cards = cardData?.userCardInfo || [];

    const [params] = useSearchParams();
    const token = params.get("q");

    if (!cardData) {
        return (
            <div className="flex justify-center items-center h-screen">
                Loading...
            </div>
        );
    }

    const handleSubmit = async () => {
        const selectedCard = cards[selectedIndex];

        if (!selectedCard) {
            alert("Please select a card");
            return;
        }
        try {
            const apiUrl = `${BASE_URL}/api/v1/cardAdded/${selectedCard.userId}/${selectedCard.objectReferenceId}?CancelCard=YES`;

            // console.log("API URL:", apiUrl);

            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            // console.log("AUTH HEADER:", `Bearer ${token}`);

            // console.log("STATUS:", response.status);

            const result = await response.json();
            // console.log("RESPONSE:", result);

            if (response.status === 403) {
                console.log("403 Forbidden Check token or permissions");
                return;
            }

            const redirectUrl = result?.redirectUrl;
            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                console.log("Return URL not found");
            }

        } catch (error) {
            console.error("ERROR:", error);
            console.log("API failed");
        }
    };


    const handleCancel = async () => {
        const confirmCancel = window.confirm(
            "Are you sure you want to cancel this Card request?"
        );
        if (!confirmCancel) return;

        try {
            const res = await fetch(`${BASE_URL}/api/v1/cancelCard`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!res.ok) throw new Error("Cancel request failed");

            const data = await res.json();
            if (data?.returnUrl) {
                window.location.href = data.returnUrl;
            }
        } catch (err) {
            console.error(err);
            alert("Failed to cancel card. Please try again.");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">

            <div className="w-full max-w-4xl p-6 bg-white rounded-lg">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">
                        Credit Card Details
                    </h1>
                    <img src={q2tLogo} alt="logo" className="h-12 object-contain" />
                </div>

                {/* Cards Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

                    {/* LEFT: Swiper */}
                    <div>

                        <div className="flex justify-between mb-3">
                            <button className="custom-prev w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-100">
                                ‹
                            </button>
                            <button className="custom-next w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-100">
                                ›
                            </button>
                        </div>

                        <Swiper
                            modules={[Pagination, Navigation]}
                            spaceBetween={20}
                            slidesPerView={1.1}
                            centeredSlides={true}
                            pagination={{ clickable: true }}
                            navigation={{
                                nextEl: ".custom-next",
                                prevEl: ".custom-prev",
                            }}
                            onSwiper={(swiper) => (swiperRef.current = swiper)}
                            onSlideChange={(swiper) => {
                                setSelectedIndex(swiper.realIndex);
                            }}
                        >

                            {cards.map((card, index) => (
                                <SwiperSlide key={index}>
                                    <CardItem
                                        number={card.cardNumber.slice(-4)}
                                        type={card.paymentMethod}
                                        status={true}
                                    />
                                </SwiperSlide>
                            ))}

                        </Swiper>
                    </div>

                    {/* RIGHT */}
                    <div className="bg-gray-50 border rounded-xl p-5 flex flex-col items-center mt-4 h-[220px] justify-center text-center">

                        <div className="text-blue-400 text-5xl mb-4">💳</div>

                        <div className="text-gray-800 font-semibold text-lg mb-4">
                            Haven’t tokenized your Card yet?
                        </div>

                        <button
                            onClick={() => navigate(`/addcard/?q=${token}`)}
                            className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-50 transition"
                        >
                            <span className="material-symbols-outlined">
                                add_circle
                            </span>
                            ADD NEW CARD
                        </button>
                    </div>

                </div>

                {/* Card List */}
                <div className="mt-8">

                    <div className="hidden md:grid grid-cols-3 text-gray-700 font-semibold mb-4">
                        <div>Card Number</div>
                        <div>Expires On</div>
                        <div>Status</div>
                    </div>

                    {cards.map((card, index) => (
                        <CardRow
                            key={index}
                            card={card}
                            index={index}
                            selectedIndex={selectedIndex}
                            setSelectedIndex={setSelectedIndex}
                            swiperRef={swiperRef}
                        />
                    ))}

                    {/* Buttons */}
                    <div className="flex gap-4 mt-4">
                        <button
                            onClick={handleCancel}
                            className="w-1/2 border border-blue-600 text-gray-800 py-3 rounded-lg hover:bg-gray-100 transition">
                            CANCEL
                        </button>
                        <button
                            type="button"
                            disabled={selectedIndex === null}
                            onClick={handleSubmit}
                            className={`w-1/2 py-3 rounded-lg flex items-center justify-center gap-2 transition ${selectedIndex === null
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-700 text-white hover:bg-blue-800"
                                }`}
                        >
                            SUBMIT
                        </button>
                    </div>

                </div>

            </div>
        </div>
    );
}